import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import { getSharedStyles } from '../../themes';
import { showErrors } from '../../modules/general';
import { search, remove } from '../../modules/apAutomation/carrierStationMapping';
import GenericConfirmDeletionDialog from '../../components/Dialogs/GenericConfirmDeletionDialog';
import StationMappingDialog from './StationMappingDialog';

const styles = (theme) => ({
	...getSharedStyles(theme),
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 10,
	},
	intro: {
		color: '#555',
		fontSize: '0.9rem',
		marginBottom: 15,
		maxWidth: 900,
	},
	matchText: {
		fontFamily: 'monospace',
		fontWeight: 600,
	},
	notes: {
		color: '#555',
		fontSize: '0.8rem',
		maxWidth: 380,
	},
	small: {
		color: '#777',
		fontSize: '0.8rem',
	},
	history: {
		fontSize: '0.8rem',
		color: '#555',
		backgroundColor: '#fafafa',
	},
	linkButton: {
		padding: '2px 8px',
		minWidth: 0,
		textTransform: 'none',
	},
});

function formatWhen(isoDate) {
	return isoDate ? moment(isoDate).format('M/D/YY h:mm a') : '';
}

/**
 * Text on a carrier's freight bill that means one of our stations, for bills that print no
 * station code (FDS names some of our locations its own way). AP adds most of these from the
 * triage screen with Map to Station; this page lists them and lets you fix or remove one.
 */
class StationMappings extends Component {
	state = {
		isLoading: true,
		mappings: [],
		collections: {},
		isDialogOpen: false,
		editingMapping: null,
		removingMapping: null,
		openHistoryId: null,
	};

	componentDidMount() {
		this.load();
	}

	load = () => {
		search({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return this.props.showErrors(err);
				}
				const { collections } = r;
				this.setState({
					isLoading: false,
					mappings: collections.carrierStationMappingCollection || [],
					collections: {
						stationMap: _.keyBy(collections.stationCollection, 'stationId'),
						carrierMap: _.keyBy(collections.carrierCollection, 'carrierId'),
						userMap: _.keyBy(collections.userCollection, 'userId'),
					},
				});
			},
		});
	};

	userName = (userId) => {
		const user = _.get(this.state.collections, ['userMap', userId]);
		return user ? `${user.firstName} ${user.lastName}` : 'system';
	};

	stationLabel = (stationId) => {
		//- Older history can name a station no current mapping uses; fall back to the full list.
		const station =
			_.get(this.state.collections, ['stationMap', stationId]) || this.props.stationMap[stationId];
		return station ? `${station.number} - ${station.name}` : `Station #${stationId}`;
	};

	describeEdit = (edit) => {
		const { action } = edit;
		const oldValue = edit.oldValue || {};
		const newValue = edit.newValue || {};
		if (action === 'add') {
			return `added "${newValue.matchText}" → ${this.stationLabel(newValue.stationId)}`;
		}
		if (action === 'remove') {
			return `removed "${oldValue.matchText}" → ${this.stationLabel(oldValue.stationId)}`;
		}
		const changes = [];
		if (oldValue.matchText !== newValue.matchText) {
			changes.push(`text "${oldValue.matchText}" → "${newValue.matchText}"`);
		}
		if (oldValue.stationId !== newValue.stationId) {
			changes.push(
				`station ${this.stationLabel(oldValue.stationId)} → ${this.stationLabel(
					newValue.stationId
				)}`
			);
		}
		if ((oldValue.notes || '') !== (newValue.notes || '')) {
			changes.push('notes');
		}
		return `changed ${changes.join(', ') || 'nothing'}`;
	};

	renderRow = (mapping) => {
		const { classes } = this.props;
		const { openHistoryId } = this.state;
		const {
			carrierStationMappingId,
			carrierId,
			matchText,
			stationId,
			notes,
			createdByUserId,
			dateCreated,
			lastUpdatedByUserId,
			lastUpdatedAt,
			edits,
		} = mapping;
		const carrier = _.get(this.state.collections, ['carrierMap', carrierId]);
		const isHistoryOpen = openHistoryId === carrierStationMappingId;

		return (
			<Fragment key={carrierStationMappingId}>
				<TableRow>
					<TableCell>{carrier ? carrier.name : `Carrier #${carrierId}`}</TableCell>
					<TableCell className={classes.matchText}>{matchText}</TableCell>
					<TableCell>{this.stationLabel(stationId)}</TableCell>
					<TableCell className={classes.notes}>{notes}</TableCell>
					<TableCell className={classes.small}>
						{lastUpdatedAt ? (
							<>
								Changed by {this.userName(lastUpdatedByUserId)}
								<br />
								{formatWhen(lastUpdatedAt)}
							</>
						) : (
							<>
								Added by {this.userName(createdByUserId)}
								<br />
								{formatWhen(dateCreated)}
							</>
						)}
						{!_.isEmpty(edits) && (
							<div>
								<Button
									size="small"
									color="primary"
									className={classes.linkButton}
									onClick={() =>
										this.setState({ openHistoryId: isHistoryOpen ? null : carrierStationMappingId })
									}
								>
									{isHistoryOpen ? 'Hide history' : `History (${edits.length})`}
								</Button>
							</div>
						)}
					</TableCell>
					<TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
						<Button
							size="small"
							color="primary"
							onClick={() => this.setState({ isDialogOpen: true, editingMapping: mapping })}
						>
							Edit
						</Button>
						<Button size="small" onClick={() => this.setState({ removingMapping: mapping })}>
							Remove
						</Button>
					</TableCell>
				</TableRow>
				{isHistoryOpen && (
					<TableRow>
						<TableCell colSpan={6} className={classes.history}>
							{edits.map((edit) => (
								<div key={edit.carrierStationMappingEditId}>
									{formatWhen(edit.dateCreated)}: {this.userName(edit.editedByUserId)}{' '}
									{this.describeEdit(edit)}
								</div>
							))}
						</TableCell>
					</TableRow>
				)}
			</Fragment>
		);
	};

	render() {
		const { classes, showErrors } = this.props;
		const { isLoading, mappings, isDialogOpen, editingMapping, removingMapping } = this.state;

		return (
			<Paper className={classes.paper}>
				<div className={classes.header}>
					<Typography variant="h5">Station Mappings</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={() => this.setState({ isDialogOpen: true, editingMapping: null })}
					>
						Add Mapping
					</Button>
				</div>
				<div className={classes.intro}>
					Some carriers (mostly FDS) name our stations their own way and print no station code on
					the bill. When a stop on a freight bill has no station code, AP Automation looks here for
					text that appears in the destination of the stop and uses that station. The station and
					gallons are still checked against our delivery, so a wrong mapping sends the bill to
					triage instead of paying it. Add new ones from the triage screen with Map to Station.
				</div>

				{isLoading && <CircularProgress size={24} />}
				{!isLoading && _.isEmpty(mappings) && <div>No station mappings yet.</div>}
				{!isLoading && !_.isEmpty(mappings) && (
					<Table className={classes.table} size="small">
						<TableHead>
							<TableRow>
								<TableCell>Carrier</TableCell>
								<TableCell>Text to look for</TableCell>
								<TableCell>Station</TableCell>
								<TableCell>Notes</TableCell>
								<TableCell>Last change</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>{mappings.map(this.renderRow)}</TableBody>
					</Table>
				)}

				<StationMappingDialog
					open={isDialogOpen}
					mapping={editingMapping}
					onClose={() => this.setState({ isDialogOpen: false })}
					onSaved={() => {
						this.setState({ isDialogOpen: false });
						this.load();
					}}
				/>

				{removingMapping && (
					<GenericConfirmDeletionDialog
						open={!!removingMapping}
						title="Remove Station Mapping"
						questionText={
							`Remove the mapping "${removingMapping.matchText}" → ` +
							`${this.stationLabel(removingMapping.stationId)}? Bills with that text and no ` +
							'station code will go to triage again.'
						}
						showErrors={showErrors}
						apiDeleteFunction={({ callback }) =>
							remove({
								carrierStationMappingId: removingMapping.carrierStationMappingId,
								callback,
							})
						}
						onClose={() => {
							this.setState({ removingMapping: null });
							this.load();
						}}
					/>
				)}
			</Paper>
		);
	}
}

StationMappings.propTypes = {
	classes: PropTypes.object.isRequired,
	stationMap: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
	stationMap: state.maps.station || {},
});

export default connect(mapStateToProps, { showErrors })(withStyles(styles)(StationMappings));
